/**
 * L13 — Lower Third
 * Classic broadcast lower-third graphic.
 * Full-screen media with an animated name/title bar in the lower portion.
 * Perfect for: reporter name cards, location identifiers, interview titles,
 *              expert quotes, eyewitness IDs, breaking-news subject cards.
 */
import React from 'react';
import { AbsoluteFill, useCurrentFrame, spring, useVideoConfig } from 'remotion';
import type { SceneProps } from '../../types';
import { SceneContainer, Visual, GlobalOverlays } from './shared';

export const L13_LowerThird: React.FC<SceneProps> = (props) => {
  const {
    headline, subheadline, assets,
    colorPalette, headlineFont, subheadlineFont,
    reporterName, location,
  } = props;

  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();

  // ── Accent stripe slides in from the left ──
  const barSpring = spring({
    frame, fps,
    config: { damping: 20, mass: 0.7, stiffness: 130 },
    durationInFrames: 22,
  });

  // ── Text fades in after the bar arrives ──
  const TEXT_DELAY = 14;
  const textOpacity = Math.min(1, Math.max(0, (frame - TEXT_DELAY) / 10));

  // ── Second line (subheadline) comes in a bit later ──
  const SUB_DELAY = 20;
  const subOpacity = Math.min(1, Math.max(0, (frame - SUB_DELAY) / 10));

  const barTranslateX = (1 - barSpring) * -1000;

  const hFontSize = Math.min(headlineFont.fontSize ?? 46, 52);
  const sFontSize = Math.min(subheadlineFont.fontSize ?? 22, 26);

  return (
    <SceneContainer bg={colorPalette.background ?? '#0d1f3c'}>

      {/* ── Full-screen background media ── */}
      <AbsoluteFill>
        <Visual asset={assets.primary} objectFit="cover" layerKey="primaryMedia" />
      </AbsoluteFill>

      {/* ── Bottom gradient darkens media behind the bar ── */}
      <AbsoluteFill style={{
        background: 'linear-gradient(to top, rgba(0,0,0,0.88) 0%, rgba(0,0,0,0.5) 28%, transparent 55%)',
        pointerEvents: 'none',
      }} />

      {/* ── Lower third bar assembly ── */}
      <AbsoluteFill style={{
        display: 'flex', flexDirection: 'column',
        justifyContent: 'flex-end',
        padding: '0 0 108px 0',        // above ticker area
      }}>

        {/* Bar row */}
        <div style={{
          transform: `translateX(${barTranslateX}px)`,
          display: 'flex', flexDirection: 'row', alignItems: 'stretch',
          width: '76%', minHeight: 96,
          boxShadow: '4px 0 24px rgba(0,0,0,0.4)',
        }}>
          {/* Accent stripe */}
          <div style={{
            width: 8, flexShrink: 0,
            background: colorPalette.accent,
          }} />

          {/* Text block */}
          <div style={{
            flex: 1,
            background: `linear-gradient(90deg, ${colorPalette.primary}f2 0%, ${colorPalette.primary}cc 70%, ${colorPalette.primary}00 100%)`,
            padding: '14px 60px 14px 22px',
            display: 'flex', flexDirection: 'column',
            justifyContent: 'center', gap: 5,
          }}>

            {/* Headline (name / topic) */}
            <div style={{
              fontFamily: headlineFont.fontFamily ?? 'Roboto, sans-serif',
              fontSize: hFontSize,
              fontWeight: 800,
              color: headlineFont.color ?? '#ffffff',
              letterSpacing: 0.3,
              lineHeight: 1.15,
              opacity: textOpacity,
              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
            }}>
              {headline}
            </div>

            {/* Subheadline (title / designation / location) */}
            {subheadline && (
              <div style={{
                fontFamily: subheadlineFont.fontFamily ?? 'Roboto, sans-serif',
                fontSize: sFontSize,
                fontWeight: 600,
                color: colorPalette.accent,
                letterSpacing: 1.5,
                textTransform: 'uppercase',
                opacity: subOpacity,
                overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
              }}>
                {subheadline}
              </div>
            )}
          </div>
        </div>

        {/* Reporter / location credit line (below bar) */}
        {(reporterName || location) && (
          <div style={{
            marginTop: 8,
            paddingLeft: 30,       // aligns with accent stripe + text offset
            opacity: subOpacity * 0.75,
            display: 'flex', alignItems: 'center', gap: 8,
          }}>
            <div style={{ width: 3, height: 14, background: colorPalette.accent, borderRadius: 2, flexShrink: 0 }} />
            <div style={{
              fontFamily: 'Roboto, sans-serif', fontSize: 17,
              color: 'rgba(255,255,255,0.65)',
              letterSpacing: 0.3,
            }}>
              {[reporterName, location].filter(Boolean).join('  ·  ')}
            </div>
          </div>
        )}
      </AbsoluteFill>

      <GlobalOverlays props={props} />
    </SceneContainer>
  );
};
